import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import {
  Button,
  CircularProgress,
  Collapse,
  DialogContentText,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import PropTypes from "prop-types";

import Backdrop from "Components/Common/Backdrop";
import Modal from "Components/Common/Modal";
import FireSafetyViewBuildingObservationDetails from "./FireSafetyViewBuildingObservationDetails";
import {
  FIRE_EMERGENCY_CHIEF_FIRE_OFFICER,
  FIRE_SAFETY_CONSUMER,
  fireSafetyApplicationStatusTypes,
} from "common/constant";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

import {
  useGetBuildingSchedulesQuery,
  useLazyFireSafetyBuildingObservationQuery,
  usePutSendComplianceReport,
  usePutSendNoticeMutation,
} from "apis/FireSafety/FireSafetyObservation";
import { isAuthorizedUser } from "utils";
import CommonUploadDocumentModal from "./CommonUploadDocumentModal";

const FireSafetyViewBuildingSchedules = ({ handleClose, applicationId, t }) => {
  const { roles } = useSelector((state) => state.Login);
  const SCHEDULE_COMPLETED = 3;

  const [sendComplianceReport, { isLoading: isSendComplianceReportLoading }] =
    usePutSendComplianceReport();
  const [sendNotice, { isLoading: isSendNotice }] = usePutSendNoticeMutation();

  const {
    data: { data: buildingSchedulesData } = {},
    isLoading,
    isFetching,
  } = useGetBuildingSchedulesQuery(applicationId);

  const completedScheduleIds = buildingSchedulesData?.map((item) => {
    if (item?.status === SCHEDULE_COMPLETED) {
      return item?.scheduleId;
    }
  });

  const [
    getBuildingObservationDataById,
    {
      isLoading: isBuildingObservationDataLoading,
      isFetching: isBuildingObservationDataFetching,
      data: { data: buildingObservationData } = {},
    },
  ] = useLazyFireSafetyBuildingObservationQuery();

  const isApprovedApplicationStatus = useMemo(
    () =>
      buildingObservationData?.buildingInformationStatusId ===
      fireSafetyApplicationStatusTypes["Approved"],
    [buildingObservationData?.buildingInformationStatusId]
  );

  const [scheduleIdForSendNotice, setScheduleIdForSendNotice] = useState(null);
  const [scheduleId, setScheduleId] = useState("");
  const [isSendComplianceModal, setIsSendComplianceModal] = useState(null);

  const handleToggle = (id) => {
    if (scheduleId === id) {
      setScheduleId("");
    } else {
      setScheduleId(id);
    }
  };

  const handleSendNotice = async (file) => {
    const formData = new FormData();
    formData.append("scheduleId", scheduleId);
    if (file) {
      formData.append("attachmentFile", file);
    }
    try {
      await sendNotice(formData);
      setScheduleIdForSendNotice(null);
      handleClose();
    } catch (error) {}
  };

  const handleSendComplianceReoprt = async (file) => {
    const formData = new FormData();
    formData.append("scheduleId", scheduleId);
    if (file) {
      formData.append("attachmentFile", file);
    }
    try {
      await sendComplianceReport(formData);
      setIsSendComplianceModal(null);
      handleClose();
    } catch (error) {}
  };

  const buildingObservation = useMemo(() => {
    const { observations = [], scheduleId } = buildingObservationData || {};

    let filteredObservations = observations;

    if (roles.includes(FIRE_SAFETY_CONSUMER)) {
      filteredObservations = observations?.filter(
        ({ isApproved }) => isApproved
      );
    }
    if (completedScheduleIds?.includes(scheduleId)) {
      return filteredObservations.map((obs) => ({
        ...obs,
        disableResolveDetailField: true,
      }));
    }
    return filteredObservations;
  }, [buildingObservationData, roles, completedScheduleIds]);

  useEffect(() => {
    if (scheduleId) {
      (async () => {
        try {
          await getBuildingObservationDataById(scheduleId);
        } catch (error) {
          console.error(error);
        }
      })();
    }
  }, [scheduleId]);

  useEffect(() => {
    if (buildingSchedulesData?.length === 1) {
      setScheduleId(buildingSchedulesData[0].scheduleId);
    }
  }, [buildingSchedulesData]);

  if (isLoading || isFetching) {
    return <Backdrop open />;
  }

  return (
    <>
      {scheduleIdForSendNotice && (
        <CommonUploadDocumentModal
          onSubmit={handleSendNotice}
          onClose={() => {
            setScheduleIdForSendNotice(null);
          }}
          handleClose={handleClose}
          isSubmitting={isSendNotice}
        />
      )}

      {isSendComplianceModal && (
        <CommonUploadDocumentModal
          onSubmit={handleSendComplianceReoprt}
          onClose={() => {
            setIsSendComplianceModal(null);
          }}
          handleClose={handleClose}
          isSubmitting={isSendComplianceReportLoading}
        />
      )}

      <Modal
        isOpen
        handleClose={handleClose}
        title={t("Building Observations")}
        maxWidth="lg"
      >
        {buildingSchedulesData?.length ? (
          buildingSchedulesData.map(
            ({
              startDate,
              endDate,
              scheduleId: id,
              isObservationNoticeResend,
              isComplicanceCanSend,
            }) => (
              <Fragment key={id}>
                <div
                  className="fs-4 fw-semibold mb-3 border-bottom"
                  onClick={() => handleToggle(id)}
                  key={id}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    className="cursor-pointer"
                  >
                    <div className="d-flex align-items-center gap-1">
                      <p className="fw-bold mb-0">{`${convertUTCDateToLocalDate(
                        startDate
                      ).format("DD-MM-YYYY")} - ${convertUTCDateToLocalDate(
                        endDate
                      ).format("DD-MM-YYYY")}`}</p>
                    </div>
                    <div>
                      {isAuthorizedUser(roles, [FIRE_SAFETY_CONSUMER]) &&
                        isComplicanceCanSend && (
                          <Button
                            sx={{ m: 2 }}
                            size="small"
                            color="primary"
                            variant="contained"
                            startIcon={
                              isSendComplianceReportLoading ? (
                                <CircularProgress size={20} />
                              ) : null
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsSendComplianceModal(true);
                            }}
                          >
                            {t("Send compliance report")}
                          </Button>
                        )}
                      <IconButton>
                        {scheduleId === id ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </div>
                  </Stack>
                </div>
                <DialogContentText variant="subtitle2" component="div">
                  <Collapse in={scheduleId === id} timeout="auto" unmountOnExit>
                    {isBuildingObservationDataFetching ||
                    isBuildingObservationDataLoading ? (
                      <div className="d-flex justify-content-center">
                        <CircularProgress size={30} />
                      </div>
                    ) : (
                      <>
                        {!!buildingObservation?.length &&
                          buildingObservation.map(
                            (observationDetails, index) => (
                              <FireSafetyViewBuildingObservationDetails
                                key={index}
                                observationDetails={observationDetails}
                                index={index}
                                buildingInformationId={
                                  buildingObservationData.buildingInformationId
                                }
                                completedScheduleIds={completedScheduleIds}
                              />
                            )
                          )}
                      </>
                    )}

                    {isObservationNoticeResend &&
                      roles.includes(FIRE_EMERGENCY_CHIEF_FIRE_OFFICER) &&
                      isApprovedApplicationStatus && (
                        <div className="text-end">
                          <Grid
                            container
                            rowSpacing={1}
                            columnSpacing={2}
                            sx={{ justifyContent: "end" }}
                          >
                            <Grid item>
                              <Button
                                color="success"
                                variant="contained"
                                onClick={() => setScheduleIdForSendNotice(true)}
                              >
                                {t("Send Notice")}
                              </Button>
                            </Grid>
                          </Grid>
                        </div>
                      )}
                  </Collapse>
                </DialogContentText>
              </Fragment>
            )
          )
        ) : (
          <Typography variant="h6" className="text-center mt-3">
            {t("There is no observations added for this building till now")}
          </Typography>
        )}
      </Modal>
    </>
  );
};

FireSafetyViewBuildingSchedules.propTypes = {
  handleClose: PropTypes.func,
  applicationId: PropTypes.number,
  t: PropTypes.func,
};

export default withTranslation()(FireSafetyViewBuildingSchedules);
